
import { FormButton, FormInput } from "@/shared/lib/form";
import { registerSchema, type RegisterFormData } from "@/shared/lib/validation";
import { useForm } from "@tanstack/react-form";
import React from "react";
import styles from "./RegisterForm.module.css";

interface RegisterFormProps {
  onSubmit?: (data: RegisterFormData) => Promise<void>;
  className?: string;
  isLoading?: boolean;
  error?: string;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSubmit,
  className = "",
  isLoading = false,
  error,
}) => {
  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value);
      }
    },
  });

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>Регистрация</h2>
          <p className={styles.subtitle}>Создайте аккаунт в Destiny Games</p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className={styles.form}
        >
          <form.Field
            name="email"
            validators={{
              onChange: ({ value }) => {
                const emailSchema = registerSchema._def.schema.shape.email;
                const result = emailSchema.safeParse(value);
                return result.success
                  ? undefined
                  : result.error.errors[0]?.message;
              },
            }}
            children={(field) => (
              <div className={styles.field}>
                <label htmlFor={field.name} className={styles.label}>
                  Email
                </label>
                <FormInput
                  id={field.name}
                  name={field.name}
                  type="email"
                  placeholder="Введите ваш email"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={() => field.handleBlur()}
                  hasError={field.state.meta.errors.length > 0}
                  disabled={isLoading}
                  className={styles.input}
                />
                {field.state.meta.errors.length > 0 && (
                  <div className={styles.errorMessage}>
                    {field.state.meta.errors.map((error, index) => (
                      <p key={index} className={styles.errorText}>
                        {String(error)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          />

          <form.Field
            name="password"
            validators={{
              onChange: ({ value }) => {
                const passwordSchema =
                  registerSchema._def.schema.shape.password;
                const result = passwordSchema.safeParse(value);
                return result.success
                  ? undefined
                  : result.error.errors[0]?.message;
              },
            }}
            children={(field) => (
              <div className={styles.field}>
                <label htmlFor={field.name} className={styles.label}>
                  Пароль
                </label>
                <FormInput
                  id={field.name}
                  name={field.name}
                  type="password"
                  placeholder="Введите пароль"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={() => field.handleBlur()}
                  hasError={field.state.meta.errors.length > 0}
                  disabled={isLoading}
                  className={styles.input}
                />
                {field.state.meta.errors.length > 0 && (
                  <div className={styles.errorMessage}>
                    {field.state.meta.errors.map((error, index) => (
                      <p key={index} className={styles.errorText}>
                        {String(error)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            validators={{
              onChange: ({ value, fieldApi }) => {
                const password = fieldApi.form.getFieldValue("password");
                const confirmPasswordSchema =
                  registerSchema._def.schema.shape.confirmPassword;
                const result = confirmPasswordSchema.safeParse(value);
                if (!result.success) {
                  return result.error.errors[0]?.message;
                }
                if (password && value && password !== value) {
                  return "Пароли не совпадают";
                }
                return undefined;
              },
            }}
            children={(field) => (
              <div className={styles.field}>
                <label htmlFor={field.name} className={styles.label}>
                  Подтвердите пароль
                </label>
                <FormInput
                  id={field.name}
                  name={field.name}
                  type="password"
                  placeholder="Повторите пароль"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={() => field.handleBlur()}
                  hasError={field.state.meta.errors.length > 0}
                  disabled={isLoading}
                  className={styles.input}
                />
                {field.state.meta.errors.length > 0 && (
                  <div className={styles.errorMessage}>
                    {field.state.meta.errors.map((error, index) => (
                      <p key={index} className={styles.errorText}>
                        {String(error)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <FormButton
                type="submit"
                isLoading={isLoading || isSubmitting}
                disabled={!canSubmit}
                className={styles.submitButton}
              >
                Зарегистрироваться
              </FormButton>
            )}
          />
        </form>


        <div className={styles.footer}>
          <p className={styles.footerText}>
            Уже есть аккаунт?{" "}
            <a href="/login" className={styles.footerLink}>
              Войти
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
